import React, { memo, useEffect, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import "./PDFViewer.css";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

const PDFViewer = ({ data: documentUrl }) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pageRotations, setPageRotations] = useState({}); // Store each page’s natural rotation
  const [extraRotation, setExtraRotation] = useState(0); // Manual rotation (user clicks)
  const [rotation, setRotation] = useState(0); // Final rotation = natural + manual

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setLoading(false);
  };

  const onPageLoadSuccess = (page) => {
    // Save the embedded rotation for this page
    setPageRotations((prev) => ({
      ...prev,
      [page.pageNumber]: page.rotate || 0,
    }));
    // Apply it immediately if it’s the current page
    if (page.pageNumber === pageNumber) {
      setRotation(((page.rotate || 0) + extraRotation) % 360);
    }
  };

  useEffect(() => {
    setLoading(true);
  }, [documentUrl]);

  // 🔄 When page number or manual rotation changes, compute effective rotation
  useEffect(() => {
    const baseRotation = pageRotations[pageNumber] || 0;
    setRotation((baseRotation + extraRotation) % 360);
  }, [pageNumber, extraRotation, pageRotations]);

  const handleFirstPage = () => setPageNumber(1);
  const handlePrevPage = () => setPageNumber((prev) => Math.max(prev - 1, 1));
  const handleNextPage = () => setPageNumber((prev) => Math.min(prev + 1, numPages));
  const handleLastPage = () => setPageNumber(numPages);
  const handleRotate = () => setExtraRotation((prev) => (prev + 90) % 360);

  const renderPdfButtons = () => (
    <div className="pdf-controls d-flex flex-wrap">
      <button
        onClick={handleFirstPage}
        className="pdf-btn"
        disabled={pageNumber === 1}
      >
        <i className="fa fa-fast-backward"></i>
      </button>
      <button
        onClick={handlePrevPage}
        className="pdf-btn"
        disabled={pageNumber === 1}
      >
        <i className="fa fa-backward"></i>
      </button>
      <span className="pdf-page-info">
        {pageNumber} / {numPages}
      </span>
      <button
        onClick={handleNextPage}
        className="pdf-btn"
        disabled={pageNumber === numPages}
      >
        <i className="fa fa-forward"></i>
      </button>
      <button
        onClick={handleLastPage}
        className="pdf-btn"
        disabled={pageNumber === numPages}
      >
        <i className="fa fa-fast-forward"></i>
      </button>
      <button onClick={handleRotate} className="pdf-btn">
        {/* <i className="fa fa-repeat"></i> Rotate icon */}
        <i className="fas fa-fw fa-rotate-right"></i> {/* Rotate icon */}
      </button>
    </div>
  );

  return (
    <div className="pdf-container">
      {renderPdfButtons()}
      {documentUrl && (
        <Document
          file={documentUrl}
          loading="Loading file..."
          onLoadSuccess={onDocumentLoadSuccess}
          className="pdf-view" // Apply the CSS class here
        >
          <Page
            key={`${pageNumber}-${rotation}`} // ✅ Force re-render on rotation change
            pageNumber={pageNumber}
            rotate={rotation}
            onLoadSuccess={onPageLoadSuccess}
            width={Math.min(window.innerWidth * 0.9, 900)}
          />
        </Document>
      )}
      {!loading && renderPdfButtons()}
    </div>
  );
};

export default memo(PDFViewer);
